import type { UserRole } from "../api/types";

/**
 * What a role can COHERENTLY be shown — never what it is allowed to do.
 *
 * The authorization decision is server-side on every request (charter S-8);
 * nothing here is a control. What this module prevents is an incoherent UI:
 * a `billing_admin` holds `billing:view`/`billing:manage` and nothing else,
 * so rendering the fleet navigation for it would produce a shell full of
 * links that can only ever answer 403. Hiding them is a UX decision that
 * mirrors the server's grant table — kept in one place so it can be checked
 * against `server/internal/authz/model.go` in review, rather than scattered
 * across components as `role === "admin"` checks.
 */

/** Roles that hold operational grants on fleet resources (containers,
 * nodes, secrets) — i.e. everyone except the billing contact. */
export function canSeeFleet(role: string | undefined): boolean {
  return role === "admin" || role === "operator" || role === "viewer";
}

/** Roles the server grants `billing:view` to (admin, billing_admin). */
export function canSeeBilling(role: string | undefined): boolean {
  return role === "admin" || role === "billing_admin";
}

/** Roles that hold create/update/delete grants on fleet resources (containers,
 * secrets, projects, services, alerts, agent sessions, etc.) per
 * `server/internal/authz/model.go`: `operator` and `admin` both hold those
 * management actions; `viewer` holds only `*:view`; `billing_admin` holds only
 * `billing:view`/`billing:manage` and none of the fleet-resource actions.
 *
 * This is UX-only (S-8) — the server enforces the real decision on every
 * request — but it must track the server's grant table exactly, or the UI
 * offers a management affordance a role will only ever get a 403 from (or,
 * worse, hides one a role is actually entitled to). Use this instead of
 * inlining `role === "admin" || role === "operator"` at call sites so the
 * two can't drift apart.
 */
export function canManageFleet(role: string | undefined): boolean {
  return role === "admin" || role === "operator";
}

/**
 * Roles the server grants `node:create` / `environment:create` to — admin
 * ONLY (`server/internal/authz/model.go`): `operator` holds `node:view` and
 * `environment:view`/`environment:promote` but neither create action.
 * `canManageFleet` is the wrong gate for "mint a join token" or "create an
 * environment" — an operator passes it, and the affordance would only ever
 * 403 (PR #634 security audit condition 1). Use this helper for any
 * fleet-administration write (enrolling a host, creating an environment) so
 * the UI and the server's grant table can't drift apart (A-12).
 */
export function canAdministerFleet(role: string | undefined): boolean {
  return role === "admin";
}

/** Where a freshly signed-in session should land. A billing contact has no
 * fleet view to land on, so it goes to the one page it can actually load;
 * every fleet role lands on Home (`RoleLanding` renders `HomePage` directly
 * for them without calling this function at all — this branch only exists
 * so the function stays total over every role, not just billing_admin). */
export function landingPathFor(role: string | undefined): string {
  return canSeeFleet(role) ? "/" : "/billing";
}

/** Human labels for the roles, shared by the account page and the admin
 * user list so they can never drift apart. */
export const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Admin",
  operator: "Operator",
  viewer: "Viewer",
  billing_admin: "Billing admin",
};
